import { useState, useEffect } from "react";
import { Loader2, LifeBuoy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { waLink } from "@/context/SettingsContext";

const TICKET_STATUS = {
  abierto: { label: "Abierto", color: "bg-amber-100 text-amber-700" },
  en_proceso: { label: "En proceso", color: "bg-blue-100 text-blue-700" },
  resuelto: { label: "Resuelto", color: "bg-emerald-100 text-emerald-700" },
  cerrado: { label: "Cerrado", color: "bg-slate-100 text-slate-600" },
};
const STATUSES = Object.keys(TICKET_STATUS);

const Tickets = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = () => { setLoading(true); api.get("/admin/tickets").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status, admin_note) => {
    try {
      await api.patch(`/admin/tickets/${id}`, { status, admin_note });
      toast.success("Ticket actualizado");
      setItems((prev) => prev.map((t) => (t.id === id ? { ...t, status, admin_note } : t)));
      if (selected?.id === id) setSelected({ ...selected, status, admin_note });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Tickets de soporte" subtitle={`${items.length} ticket(s)`} />
      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : items.length === 0 ? <EmptyState icon={LifeBuoy} text="Sin tickets todavía." />
        : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((t) => (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`ticket-${t.ticket_number}`}>
                <div className="flex justify-between items-start mb-2">
                  <p className="font-semibold text-slate-900">#{t.ticket_number}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TICKET_STATUS[t.status]?.color}`}>{TICKET_STATUS[t.status]?.label}</span>
                </div>
                <p className="text-xs font-medium text-indigo-600">{t.subject}</p>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.message}</p>
                <p className="text-xs text-slate-400 mt-2">@{t.instagram_username} · {t.whatsapp}{t.order_number ? ` · Pedido #${t.order_number}` : ""}</p>
                <p className="text-xs text-slate-400">{formatDateTime(t.created_at)}</p>
                <Button variant="outline" size="sm" className="rounded-lg w-full mt-3" onClick={() => { setSelected(t); setNote(t.admin_note || ""); }} data-testid={`ticket-open-${t.ticket_number}`}>Ver y responder</Button>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle>Ticket #{selected.ticket_number}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Motivo</span><span className="font-medium text-slate-800">{selected.subject}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Instagram</span><span className="font-medium">@{selected.instagram_username}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">WhatsApp</span><span className="font-medium">{selected.whatsapp}</span></div>
                {selected.email && <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium">{selected.email}</span></div>}
                {selected.order_number && <div className="flex justify-between"><span className="text-slate-500">Pedido</span><span className="font-medium">#{selected.order_number}</span></div>}
                <div className="rounded-lg bg-slate-50 p-3 text-slate-700">{selected.message}</div>
              </div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota interna (opcional)" className="rounded-lg" data-testid="ticket-admin-note" />
              <div className="flex items-center gap-2">
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v, note)}>
                  <SelectTrigger className="rounded-lg flex-1" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{TICKET_STATUS[st].label}</SelectItem>)}</SelectContent>
                </Select>
                <a href={waLink(selected.whatsapp, `Hola, te contactamos de Aumenta Ya por tu ticket #${selected.ticket_number}.`)} target="_blank" rel="noreferrer">
                  <Button className="rounded-lg bg-[#25D366] hover:bg-[#20bd5a]"><MessageCircle className="h-4 w-4" /></Button>
                </a>
              </div>
              <DialogFooter>
                <Button onClick={() => changeStatus(selected.id, selected.status, note)} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="ticket-save-note">Guardar nota</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tickets;
